import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import MuiChip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import { alpha, styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { SpacingMultiple } from '../constants';

import type { FunctionComponent, MouseEventHandler } from 'react';

const StyledLabel = styled(Grid)(({ theme }) => ({
    alignItems: 'center',
    flexWrap: 'nowrap',
    gap: theme.spacing(SpacingMultiple.small),
    justifyContent: 'center',
    paddingInline: 0,
}));

const bgOpacity = 0.04;

const StyledMuiChip = styled(MuiChip)(({ theme }) => ({
    '&.inactive': {
        borderColor: 'transparent',
        [`${StyledLabel}`]: { opacity: 0.6 },
    },

    backgroundColor: alpha(theme.palette.text.primary, bgOpacity),
    borderColor: theme.palette.text.primary,
    borderStyle: 'solid',
    borderWidth: 1,
    color: theme.palette.text.primary,
    cursor: 'pointer',
    fontSize: 14,
}));

type ToggleChipLabelProps = {
    readonly isActive?: boolean;
    readonly text: string;
};

const ToggleChipLabel: FunctionComponent<ToggleChipLabelProps> = ({ isActive, text }) => (
    <StyledLabel container={true}>
        <Typography
            sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            }}
            variant="body1"
        >
            {text}
        </Typography>
        {isActive === true && <CheckCircleIcon fontSize="small" sx={{ mr: -0.5 }} />}
    </StyledLabel>
);

type ToggleChipProps = {
    readonly borderSize?: number;
    readonly disabled?: boolean;
    readonly isActive?: boolean | undefined;
    readonly label: string;
    readonly onClick: MouseEventHandler;
};

export const ToggleChip: FunctionComponent<ToggleChipProps> = ({ disabled, isActive = false, label, onClick }) => (
    <StyledMuiChip
        aria-label="toggle-chip"
        className={!isActive ? 'inactive' : ''}
        disabled={disabled}
        label={<ToggleChipLabel isActive={isActive} text={label} />}
        size="small"
        sx={{ borderStyle: 'solid 2px' }}
        onClick={onClick}
    />
);
